import { Helmet } from "react-helmet";

const Terms = () => {
  return (
      <>
        <Helmet>
          <title>Terms of Service | SurpriseTable</title>
          <meta
              name="description"
              content="Review SurpriseTable’s Terms of Service, your legal agreement when booking, using our platform, or interacting with our services."
          />
        </Helmet>
        <section className="bg-white py-16 transition-colors duration-200 dark:bg-gray-900">
          <div className="container mx-auto max-w-3xl px-4">
            <h1 className="mb-8 font-serif text-4xl font-bold text-gray-800 dark:text-white text-center">
              Terms of Service
            </h1>
            <div className="space-y-8 text-gray-700 dark:text-gray-200 text-base">

              {/* 1. Introduction */}
              <div>
                <h2 className="mb-2 text-2xl font-semibold text-primary dark:text-primary-dark">
                  1. Introduction
                </h2>
                <p>
                  Welcome to SurpriseTable, operated by <b>Deviofy FZCO</b>, Dubai Silicon Oasis, Dubai, UAE.
                  By accessing or using our website, mobile app, or any SurpriseTable services, you agree to these Terms of Service (“Terms”).
                  Please read them carefully. If you do not agree, do not use our services.
                </p>
              </div>

              {/* 2. Eligibility */}
              <div>
                <h2 className="mb-2 text-2xl font-semibold text-primary dark:text-primary-dark">
                  2. Eligibility
                </h2>
                <p>
                  You must be at least 16 years old and legally capable of entering a binding contract to use SurpriseTable.
                  By using the platform, you represent and warrant that you meet these requirements.
                </p>
              </div>

              {/* 3. Service Overview */}
              <div>
                <h2 className="mb-2 text-2xl font-semibold text-primary dark:text-primary-dark">
                  3. Service Overview
                </h2>
                <p>
                  SurpriseTable is a booking platform that connects users with participating restaurants for surprise dining experiences.
                  The restaurant choice is revealed to you only after booking. All reservations and payment arrangements are subject to these terms and any additional terms provided during booking.
                </p>
              </div>

              {/* 4. Booking & Payments */}
              <div>
                <h2 className="mb-2 text-2xl font-semibold text-primary dark:text-primary-dark">
                  4. Bookings and Payments
                </h2>
                <ul className="list-disc pl-6 space-y-2">
                  <li>
                    A fixed booking fee (e.g. €15 per person) is payable in advance via the platform. The fee is credited toward your restaurant bill, unless otherwise stated.
                  </li>
                  <li>
                    All payments are securely processed by third-party providers (Stripe, PayPal, etc.). SurpriseTable does <b>not</b> store card details.
                  </li>
                  <li>
                    Reservations are not confirmed until payment is successful and you receive a confirmation email.
                  </li>
                  <li>
                    Restaurant participation and table availability are subject to change.
                  </li>
                </ul>
              </div>

              {/* 5. Cancellations & Refunds */}
              <div>
                <h2 className="mb-2 text-2xl font-semibold text-primary dark:text-primary-dark">
                  5. Cancellations and Refunds
                </h2>
                <ul className="list-disc pl-6 space-y-2">
                  <li>
                    You may cancel your booking up to 24 hours before your reservation time for a full refund of your booking fee.
                  </li>
                  <li>
                    Cancellations within 24 hours of the reservation or no-shows are non-refundable.
                  </li>
                  <li>
                    If a restaurant cancels your booking or is unable to accommodate, we will either reschedule your experience or issue a full refund.
                  </li>
                  <li>
                    Contact <a href="mailto:info@deviofy.com" className="text-primary hover:underline">info@deviofy.com</a> for any refund requests or issues.
                  </li>
                </ul>
              </div>

              {/* 6. User Responsibilities */}
              <div>
                <h2 className="mb-2 text-2xl font-semibold text-primary dark:text-primary-dark">
                  6. User Responsibilities
                </h2>
                <ul className="list-disc pl-6 space-y-2">
                  <li>
                    Provide accurate, complete, and up-to-date information during booking.
                  </li>
                  <li>
                    Notify SurpriseTable or the restaurant of any allergies or dietary restrictions.
                  </li>
                  <li>
                    Respect the booking time, restaurant rules, and staff.
                  </li>
                  <li>
                    You are responsible for any additional costs beyond the booking fee at the restaurant.
                  </li>
                  <li>
                    Do not misuse the platform for unlawful, abusive, or fraudulent activities.
                  </li>
                </ul>
              </div>

              {/* 7. Restaurant Partners */}
              <div>
                <h2 className="mb-2 text-2xl font-semibold text-primary dark:text-primary-dark">
                  7. Restaurant Partners
                </h2>
                <p>
                  Restaurant menus, experiences, and service levels are managed by independent partner restaurants. SurpriseTable is not responsible for the food, service quality, or restaurant conduct.
                  Any issues should be reported, and we will assist in resolution where possible.
                </p>
              </div>

              {/* 8. Referral & Credits */}
              <div>
                <h2 className="mb-2 text-2xl font-semibold text-primary dark:text-primary-dark">
                  8. Referral Program and Credits
                </h2>
                <ul className="list-disc pl-6 space-y-2">
                  <li>
                    Referral credits are subject to fair use and anti-abuse policies.
                  </li>
                  <li>
                    Credits earned have no cash value and may only be redeemed on the platform, subject to expiration and additional terms.
                  </li>
                  <li>
                    SurpriseTable reserves the right to withhold, cancel, or adjust credits if fraud or misuse is detected.
                  </li>
                </ul>
              </div>

              {/* 9. Intellectual Property */}
              <div>
                <h2 className="mb-2 text-2xl font-semibold text-primary dark:text-primary-dark">
                  9. Intellectual Property
                </h2>
                <p>
                  All content on the platform—including branding, software, graphics, and text—is the exclusive property of Deviofy FZCO or its licensors.
                  You may not copy, reproduce, or use our content without written permission.
                </p>
              </div>

              {/* 10. Privacy */}
              <div>
                <h2 className="mb-2 text-2xl font-semibold text-primary dark:text-primary-dark">
                  10. Privacy
                </h2>
                <p>
                  Our collection and use of your data is described in our <a href="/privacy" className="text-primary hover:underline">Privacy Policy</a>.
                  By using SurpriseTable, you consent to these practices.
                </p>
              </div>

              {/* 11. Limitation of Liability */}
              <div>
                <h2 className="mb-2 text-2xl font-semibold text-primary dark:text-primary-dark">
                  11. Limitation of Liability
                </h2>
                <p>
                  To the fullest extent permitted by law, Deviofy FZCO and SurpriseTable are not liable for any indirect, incidental, or consequential damages resulting from your use of the platform or restaurant experiences.
                  Total liability shall not exceed the booking fee paid by you.
                </p>
              </div>

              {/* 12. Indemnification */}
              <div>
                <h2 className="mb-2 text-2xl font-semibold text-primary dark:text-primary-dark">
                  12. Indemnification
                </h2>
                <p>
                  You agree to indemnify and hold harmless Deviofy FZCO, its affiliates, and partners from any claims, damages, or expenses arising from your misuse of SurpriseTable or violation of these Terms.
                </p>
              </div>

              {/* 13. Modifications */}
              <div>
                <h2 className="mb-2 text-2xl font-semibold text-primary dark:text-primary-dark">
                  13. Modifications
                </h2>
                <p>
                  SurpriseTable reserves the right to update or modify these Terms at any time.
                  Changes will be effective upon posting or notification. Continued use of the platform means acceptance of the new Terms.
                </p>
              </div>

              {/* 14. Governing Law & Dispute Resolution */}
              <div>
                <h2 className="mb-2 text-2xl font-semibold text-primary dark:text-primary-dark">
                  14. Governing Law and Dispute Resolution
                </h2>
                <p>
                  These Terms are governed by the laws of Dubai, United Arab Emirates.
                  Any disputes arising under or in connection with these Terms shall be subject to the exclusive jurisdiction of the courts of Dubai, UAE.
                </p>
              </div>

              {/* 15. Contact */}
              <div>
                <h2 className="mb-2 text-2xl font-semibold text-primary dark:text-primary-dark">
                  15. Contact Us
                </h2>
                <p>
                  For any questions or concerns about these Terms, email us at <a href="mailto:info@deviofy.com" className="text-primary hover:underline">info@deviofy.com</a>.
                  <br />
                  Address: Deviofy FZCO, DSO-THUB, Dubai Digital Park, Building A2, Dubai Silicon Oasis, Dubai, UAE.
                </p>
              </div>

            </div>
          </div>
        </section>
      </>
  );
};

export default Terms;
